/**
 * Wohin die Anfragen gehen - für alle Aufrufe der Engine gemeinsam.
 *
 * Eigener Baustein, weil es genau eine Antwort auf diese Frage geben darf.
 * Vorher stand die Ermittlung in api.ts, und tracking.ts hatte seine eigene,
 * kürzere Fassung: nur die Bau-Variable, sonst leer. Die ist bei uns nicht
 * gesetzt, also gingen alle Sitzungs- und Ereignis-Aufrufe an die Adresse der
 * einbettenden Seite - auf fachbetriebe-saar.de an WordPress, das mit 404
 * antwortete.
 *
 * Aufgefallen ist es nicht, weil dieser Weg jeden Fehler verschluckt: Ein
 * verlorenes Ereignis darf den Funnel nicht aufhalten. Also lief alles
 * scheinbar rund, während in Wahrheit keine einzige Sitzung entstand - und
 * ohne Sitzung keine Herkunft am Lead, also keine Zuordnung zur Anzeige.
 */

/**
 * Wohin die Anfragen gehen.
 *
 * Leer bedeutet: an dieselbe Adresse, von der die Seite kam.
 *
 * Auf einer fremden Seite stimmt das nicht mehr - dort käme die Anfrage bei
 * der fremden Domain an statt bei uns. Die Adresse muss also von woanders
 * kommen, und die einzige Angabe, die bei jeder Einbettung ohnehin schon
 * dasteht, ist das Skript selbst: Wer `embed.js` von
 * funnel.fachbetriebe-saar.de lädt, meint auch die API dort.
 *
 * Daher diese Reihenfolge:
 *
 *   1. `data-api` am Skript-Tag  - für Testaufbauten und Staging
 *   2. VITE_API_BASIS beim Bauen - für eigene Auslieferungen
 *   3. die Herkunft des Skripts  - der Normalfall auf fremden Seiten
 *   4. dieselbe Herkunft         - wenn der Funnel unter unserer Domain läuft
 *
 * Der dritte Punkt ist der eigentliche Gewinn: Es gibt genau ein Bündel für
 * alle Seiten. Ein eigenes Bündel je Kunde wäre eine Sorte Datei, die man beim
 * Ändern zuverlässig irgendwo vergisst.
 */
const umgebung = import.meta.env as Record<string, string | undefined>;

function eigenesSkript(): HTMLScriptElement | null {
  const jetzt = document.currentScript;
  if (jetzt instanceof HTMLScriptElement) return jetzt;
  /**
   * Nach dem Laden ist currentScript leer - etwa wenn eine Seite das Bündel
   * nachträglich einfügt oder ein Optimierungs-Plugin es zusammenfasst. Dann
   * bleibt die Suche über den Dateinamen.
   */
  return document.querySelector<HTMLScriptElement>('script[src*="embed.js"]');
}

function basisErmitteln(): string {
  const skript = eigenesSkript();

  const angegeben = skript?.dataset["api"];
  if (angegeben) return angegeben.replace(/\/+$/, "");

  const gebaut = umgebung["VITE_API_BASIS"];
  if (gebaut) return gebaut.replace(/\/+$/, "");

  if (skript?.src) {
    try {
      const herkunft = new URL(skript.src, window.location.href).origin;
      // Gleiche Herkunft: relativ bleiben. Das spart den Vorflug-Aufruf, den
      // der Browser vor jeder fremden Adresse zusätzlich schickt.
      if (herkunft !== window.location.origin) return herkunft;
    } catch {
      // Ein Skript-Tag ohne brauchbares src. Dann eben relativ.
    }
  }

  return "";
}

export const BASIS = basisErmitteln();
